/**
 * Curved edges (quadratic Bezier) rendered as a single dynamic LineSegments.
 *
 * One graph edge can represent a MOVE SLEEVE — a group of moves between the
 * same pair of positions that collapses into a single arc by default and
 * fans out into N sub-arcs on hover (or when the HUD toggle is set to
 * 'expanded'). To support this, we allocate `MAX_SUB_ARCS` worth of vertex
 * slots per edge in the buffer; sub-arcs that aren't currently visible get
 * their vertices collapsed to the source position (degenerate zero-length
 * segments that don't render).
 *
 * Visual effects layered onto each visible sub-arc, in compositing order:
 *
 *  1. Base color — either the source-zone → target-zone gradient
 *     (direction toggle ON) or a flat muted edge tint (direction OFF).
 *  2. Always-on warm/cool directional gradient (only when direction is ON).
 *  3. Active-edge pulse — incident edges of the focal node get a stronger
 *     warm/cool ramp pulsing at GLOW_HZ.
 *  4. Traveling glow comet — small bright spot that travels source → target
 *     on every visible sub-arc, conveying flow direction at a glance. Tied
 *     to the direction toggle (no glow in flat mode). Each sleeve and each
 *     sub-arc gets a phase offset so the comets don't all flash in lockstep.
 *
 * Pointer hit-testing decodes (sleeveIdx, subArcIdx) from the hit vertex
 * index — hovering a sleeve sets `hoveredSleeveIndex` (so just that one
 * sleeve fans out even when the default is collapsed) and sets
 * `previewMoveId` to the specific sub-arc's move so the rest of the scene
 * lights up appropriately. Clicking selects that specific move.
 */
import { useEffect, useMemo, useRef } from 'react';
import { useFrame, type ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import type { Graph, Zone } from '../types/domain';
import { ZONE_COLORS, COUNTER_COLOR, EDGE_DIM } from '../zones/palette';
import type { ForceLayout } from './useForceLayout';
import { useAppStore } from '../state/store';
import { subArcControl } from './bezier';
import { dragState } from '../state/drag';
import { nodeRadius } from './sizing';

// Tiny clearance past the node's visual radius. Just enough to keep
// the edge tip from z-fighting with the sphere surface; the line
// terminates effectively ON the surface.
const EDGE_NODE_PADDING = 0.03;

const SEGMENTS_PER_EDGE = 14;
// Active-edge pulse range — applied to edges incident to the focal node.
const GLOW_MIN = 0.30;
const GLOW_MAX = 0.85;
const GLOW_BIAS = (GLOW_MIN + GLOW_MAX) / 2;
const GLOW_AMP = (GLOW_MAX - GLOW_MIN) / 2;
const GLOW_HZ = 1.5;
// Non-incident edges hidden when an active set exists.
const DIM_FACTOR = 0.0;
// Sibling sub-arcs in the SAME expanded sleeve as the focal move
// stay visible but dimmed — they're part of the same bundle and the
// user benefits from seeing their existence (which moves share this
// from→to pair). 0.0 made them invisible; 0.35 keeps them as quiet
// background context against which the focal arc (still pulsed +
// optionally white-blended) reads as the bright foreground.
const SLEEVE_SIBLING_DIM = 0.35;
// Falloff exponent for active-edge directional tint. Concentrates the
// tint near the active end so the far end clearly stays at base color.
const DIR_FALLOFF = 1.6;
// Always-on directional tint intensity — applied to EVERY edge regardless
// of selection. Warm at source end, cool at target end with a continuous
// gradient. At 1.0 the edge would fully be the accent color; lower values
// blend with the base zone color. 0.60 reads cleanly without overwhelming
// the muted hub colors. Suppressed entirely when the HUD direction toggle
// is off.
const ALWAYS_ACCENT = 0.60;

// Master gate for the warm/cool color gradient that used to layer over
// every edge when direction was ON. Currently shelved — the traveling
// comet alone conveys directionality, and the gradient added color
// noise the user didn't want. All the gradient code below stays intact
// so flipping this back to `true` reinstates the previous look.
// (Also re-flips the baseColors vs. flatColors choice.)
const ENABLE_DIRECTION_GRADIENT = false;

// ----- Sleeve / sub-arc constants -----
// MAX_SUB_ARCS allocates this many sub-arc geometry slots per edge.
// Sleeves with fewer moves leave the unused slots degenerate. The current
// dataset's largest sleeve has 4 moves; 12 buys headroom without much
// memory cost (12 × 28 verts × 3 floats × 80 edges ≈ 350 KB total).
const MAX_SUB_ARCS = 12;
const VERTS_PER_SUB_ARC = SEGMENTS_PER_EDGE * 2;
const FLOATS_PER_SUB_ARC = VERTS_PER_SUB_ARC * 3;
const FLOATS_PER_SLEEVE = MAX_SUB_ARCS * FLOATS_PER_SUB_ARC;
// Lateral offset between adjacent sub-arcs in a small (N < 6) sleeve.
// Radius of the ring around the chord for cylindrical-fan sleeves (N ≥ 6).
// Threshold at which sleeves switch from a flat linear fan to a cylinder.

// ----- Traveling-glow constants -----
// The comet is gated by the direction toggle — when direction is on,
// both the warm/cool gradient and the moving comet appear together;
// when off, edges paint as static flat color.
//
// Speed is constant in EDGE-LENGTH units (not screen-space): one
// full edge traversal in ~1/SPEED seconds, regardless of camera
// zoom. Previously we scaled the speed by camera distance so the
// comet's screen-space speed stayed perceptible — user feedback
// in #d7a9490 removed that scaling because the changing speed
// felt jittery during a zoom drag. The speed is now ABSOLUTE.
const GLOW_TRAVEL_SPEED = 0.7;
// Falloff sharpness — lower = longer comet tail. Bumped sharpness
// DOWN from 6.0 → 3.0 so the trail extends further behind the head
// (visible trail length ≈ 1/FALLOFF of the edge). At 3.0 the comet
// reads as a bright streak with a clear leading head rather than a
// tight dot, which is what makes the direction unmissable.
const GLOW_TRAVEL_FALLOFF = 3.0;
// Blend amount toward white at the comet's peak. Capped at 1.0
// (full saturation) — the head pops to pure white over the base
// edge color and the exponential trail blends back into the base.
const GLOW_TRAVEL_AMOUNT = 1.0;

const TMP_COL_A = new THREE.Color();
const TMP_COL_B = new THREE.Color();
const TMP_COL = new THREE.Color();

function zoneColor(z: string | undefined): THREE.Color {
  if (!z) return new THREE.Color(EDGE_DIM);
  return new THREE.Color(ZONE_COLORS[z as Zone] ?? EDGE_DIM);
}

// Direction-coding accent tints. Applied across every edge as a
// continuous warm→cool gradient (source = warm, target = cool). On
// incident edges the appropriate end is replaced by the pulse-driven
// ramp for the focal-node accent. Saturated enough to read against the
// muted base zone colors even at line thickness 1; brightening these
// is the lever to pull when direction reads as too subtle.
const OUTGOING_ACCENT = new THREE.Color('#ff9438');
const INCOMING_ACCENT = new THREE.Color('#3fb4e8');

type EdgeDir = 'out' | 'in' | 'neutral';

type Props = {
  graph: Graph;
  layout: ForceLayout;
  glowEdges: Map<number, EdgeDir>;
  // When true, non-glow edges keep rendering at base color instead of
  // being hidden while a glow set is active (gameplan line highlight).
  additiveGlow?: boolean;
  // When true, edges render even in the idle "edges: off" state — used so
  // the gameplan-filtered graph shows its connecting moves.
  forceEdges?: boolean;
};

export function Edges({
  graph,
  layout,
  glowEdges,
  additiveGlow = false,
  forceEdges = false,
}: Props) {
  const geomRef = useRef<THREE.BufferGeometry>(null!);
  // Must match what Nodes.tsx draws, or edges end short of the sphere
  // or plunge through it — see the note at the top of sizing.ts.
  const sizeBy = useAppStore((s) => s.sizeBy);
  const setSelection = useAppStore((s) => s.setSelection);
  const setPreviewMoveId = useAppStore((s) => s.setPreviewMoveId);
  const setHoveredSleeveIndex = useAppStore((s) => s.setHoveredSleeveIndex);
  const showEdgeDirection = useAppStore((s) => s.showEdgeDirection);
  const sleevesExpanded = useAppStore((s) => s.sleevesExpanded);
  const hoveredSleeveIndex = useAppStore((s) => s.hoveredSleeveIndex);
  const showEdges = useAppStore((s) => s.showEdges);
  const previewMoveId = useAppStore((s) => s.previewMoveId);
  const selection = useAppStore((s) => s.selection);

  // Mirror reactive state to refs so the per-frame loop reads fresh
  // values without re-running useFrame's closure.
  const showEdgeDirectionRef = useRef(showEdgeDirection);
  const sleevesExpandedRef = useRef(sleevesExpanded);
  const hoveredSleeveIndexRef = useRef(hoveredSleeveIndex);
  const showEdgesRef = useRef(showEdges);
  // "Focal move id" — the specific move within a sleeve the user is
  // pointing at right now. Preview (hover) wins over selection so a
  // mouseover in the drawer / list takes priority over the parked
  // selection.
  const focalMoveIdRef = useRef<string | null>(null);
  useEffect(() => {
    showEdgeDirectionRef.current = showEdgeDirection;
  }, [showEdgeDirection]);
  useEffect(() => {
    sleevesExpandedRef.current = sleevesExpanded;
  }, [sleevesExpanded]);
  useEffect(() => {
    hoveredSleeveIndexRef.current = hoveredSleeveIndex;
  }, [hoveredSleeveIndex]);
  useEffect(() => {
    showEdgesRef.current = showEdges;
  }, [showEdges]);
  useEffect(() => {
    focalMoveIdRef.current =
      previewMoveId ?? (selection.kind === 'move' ? selection.id : null);
  }, [previewMoveId, selection]);

  // Decode (sleeveIdx, subArcIdx) from a raycast intersection.index.
  // LineSegments interprets consecutive vertex PAIRS as segments — the
  // hit `index` points at the start vertex of the hit segment.
  const decodeHit = (
    idx: number | undefined,
  ): { sleeveIdx: number; subArcIdx: number } | null => {
    if (idx === undefined || idx < 0) return null;
    const sleeveIdx = Math.floor(idx / (MAX_SUB_ARCS * VERTS_PER_SUB_ARC));
    if (sleeveIdx < 0 || sleeveIdx >= graph.edges.length) return null;
    const subArcIdx = Math.floor(
      (idx - sleeveIdx * MAX_SUB_ARCS * VERTS_PER_SUB_ARC) / VERTS_PER_SUB_ARC,
    );
    return { sleeveIdx, subArcIdx };
  };

  // Resolve the specific moveId for a hit. For a non-sleeve edge falls
  // back to the edge's default moveId.
  const resolveMoveId = (sleeveIdx: number, subArcIdx: number): string | undefined => {
    const edge = graph.edges[sleeveIdx];
    if (!edge) return undefined;
    if (edge.moveIds && edge.moveIds.length > 0) {
      return edge.moveIds[Math.min(subArcIdx, edge.moveIds.length - 1)];
    }
    return edge.moveId;
  };

  // Deferred clear for hover state. When the pointer crosses between
  // two sub-arcs of an expanded sleeve, `pointerout` for the old arc
  // fires *before* `pointerover` for the new one — clearing immediately
  // would collapse the sleeve for one frame and then re-expand on the
  // next pointerover, producing the visible "glitch." A short timeout
  // bridges this gap: if a new pointerover fires within HOVER_CLEAR_MS
  // we cancel the clear and the sleeve stays smoothly expanded.
  const HOVER_CLEAR_MS = 220;
  const clearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelClear = () => {
    if (clearTimerRef.current !== null) {
      clearTimeout(clearTimerRef.current);
      clearTimerRef.current = null;
    }
  };
  useEffect(() => {
    // Cancel any pending clear on unmount so we don't write state into
    // a torn-down store.
    return () => cancelClear();
  }, []);

  // When an active set exists (any node/edge is selected or being
  // previewed), non-incident edges are visually hidden. We also
  // suppress their pointer events here so the user can move the
  // cursor across them without triggering unwanted hover-previews.
  // Nodes stay fully interactive — to "escape" a stuck selection the
  // user clicks a different node, clicks empty space, or closes the
  // drawer.
  //
  // ALSO: when the HUD `edges: off` toggle is on AND we're in the
  // idle "global" state (no glow set), every edge is hidden — so
  // pointer events on all edges are suppressed too (you shouldn't
  // hover what you can't see).
  const isInteractiveEdge = (sleeveIdx: number): boolean => {
    // With the gameplan filter forcing all edges visible, they're all
    // interactive regardless of the glow set.
    if (forceEdges) return true;
    if (glowEdges.size === 0) {
      return showEdges;
    }
    return glowEdges.has(sleeveIdx);
  };

  const onPointerOver = (e: ThreeEvent<PointerEvent>) => {
    // Suppress from the first frame of a press, not just after the
    // 5px drag threshold — otherwise a pan starting near an edge
    // flashes a preview on the first sweep.
    if (dragState.isPressed) return;
    const hit = decodeHit(e.index);
    if (!hit) return;
    if (!isInteractiveEdge(hit.sleeveIdx)) return;
    const edge = graph.edges[hit.sleeveIdx];
    if (!edge) return;
    const moveId = resolveMoveId(hit.sleeveIdx, hit.subArcIdx);
    if (!moveId) return;
    e.stopPropagation();
    // Cancel any pending clear from a recent pointerout — we just
    // moved onto a (possibly different) sub-arc of the same sleeve.
    cancelClear();
    // Mark this sleeve as locally hovered so it fans out even when the
    // default state is 'collapsed'.
    if (edge.moveIds && edge.moveIds.length > 1) {
      setHoveredSleeveIndex(hit.sleeveIdx);
    }
    setPreviewMoveId(moveId);
    document.body.style.cursor = 'pointer';
  };
  const onPointerOut = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    // Defer the actual state clear so a quick crossover to a neighbor
    // sub-arc doesn't make the sleeve collapse for a frame.
    cancelClear();
    clearTimerRef.current = setTimeout(() => {
      setPreviewMoveId(null);
      setHoveredSleeveIndex(null);
      document.body.style.cursor = 'default';
      clearTimerRef.current = null;
    }, HOVER_CLEAR_MS);
  };
  const onClick = (e: ThreeEvent<MouseEvent>) => {
    if (dragState.isDragging) return;
    const hit = decodeHit(e.index);
    if (!hit) return;
    if (!isInteractiveEdge(hit.sleeveIdx)) return;
    const edge = graph.edges[hit.sleeveIdx];
    if (!edge) return;
    e.stopPropagation();
    const state = useAppStore.getState();
    if (edge.kind === 'counter-fork' && edge.counterId && edge.moveId) {
      // Re-clicking the already-selected counter is a no-op — see
      // Nodes.tsx; the in-scene card it used to toggle is gone.
      if (
        state.selection.kind === 'counter' &&
        state.selection.id === edge.counterId &&
        state.selection.moveId === edge.moveId
      ) {
        return;
      }
      setSelection({ kind: 'counter', id: edge.counterId, moveId: edge.moveId });
      return;
    }
    const moveId = resolveMoveId(hit.sleeveIdx, hit.subArcIdx);
    if (!moveId) return;
    if (state.selection.kind === 'move' && state.selection.id === moveId) return;
    setSelection({ kind: 'move', id: moveId });
  };

  // ----- Allocate buffers (size = edgeCount × MAX_SUB_ARCS × verts × 3) -----
  const { positions, baseColors, flatColors, liveColors, edgeCount, edgeRadii } = useMemo(() => {
    const edgeCount = graph.edges.length;
    const totalFloats = edgeCount * FLOATS_PER_SLEEVE;
    const positions = new Float32Array(totalFloats);
    const baseColors = new Float32Array(totalFloats);
    const flatColors = new Float32Array(totalFloats);
    // [srcRadius, tgtRadius] per edge — used in the per-frame loop to
    // clip the bezier sampling so each rendered arc stops just outside
    // its endpoint nodes' spheres. Nodes don't change radius at
    // runtime, so this is cached here.
    const edgeRadii = new Float32Array(edgeCount * 2);

    const FLAT_BASE = new THREE.Color(EDGE_DIM);
    const FLAT_COUNTER = new THREE.Color(COUNTER_COLOR).multiplyScalar(0.55);

    graph.edges.forEach((e, sleeveIdx) => {
      const src = graph.byId.get(e.source);
      const tgt = graph.byId.get(e.target);
      edgeRadii[sleeveIdx * 2 + 0] = src ? nodeRadius(src, sizeBy) : 0;
      edgeRadii[sleeveIdx * 2 + 1] = tgt ? nodeRadius(tgt, sizeBy) : 0;
      TMP_COL_A.copy(zoneColor(src?.zone));
      TMP_COL_B.copy(zoneColor(tgt?.zone));

      let flat = FLAT_BASE;
      if (e.kind === 'counter-fork') {
        TMP_COL_A.set(COUNTER_COLOR);
        TMP_COL_B.set(COUNTER_COLOR);
        flat = FLAT_COUNTER;
      } else if (e.kind === 'intended-continuation') {
        TMP_COL_A.multiplyScalar(0.35);
        TMP_COL_B.multiplyScalar(0.35);
      }

      // Same colors for every sub-arc within this sleeve (sub-arcs only
      // differ in geometry, not in tint).
      for (let arcIdx = 0; arcIdx < MAX_SUB_ARCS; arcIdx++) {
        const arcBase = sleeveIdx * FLOATS_PER_SLEEVE + arcIdx * FLOATS_PER_SUB_ARC;
        for (let s = 0; s < SEGMENTS_PER_EDGE; s++) {
          const t0 = s / SEGMENTS_PER_EDGE;
          const t1 = (s + 1) / SEGMENTS_PER_EDGE;
          TMP_COL.copy(TMP_COL_A).lerp(TMP_COL_B, t0);
          baseColors[arcBase + s * 6 + 0] = TMP_COL.r;
          baseColors[arcBase + s * 6 + 1] = TMP_COL.g;
          baseColors[arcBase + s * 6 + 2] = TMP_COL.b;
          TMP_COL.copy(TMP_COL_A).lerp(TMP_COL_B, t1);
          baseColors[arcBase + s * 6 + 3] = TMP_COL.r;
          baseColors[arcBase + s * 6 + 4] = TMP_COL.g;
          baseColors[arcBase + s * 6 + 5] = TMP_COL.b;

          flatColors[arcBase + s * 6 + 0] = flat.r;
          flatColors[arcBase + s * 6 + 1] = flat.g;
          flatColors[arcBase + s * 6 + 2] = flat.b;
          flatColors[arcBase + s * 6 + 3] = flat.r;
          flatColors[arcBase + s * 6 + 4] = flat.g;
          flatColors[arcBase + s * 6 + 5] = flat.b;
        }
      }
    });

    const liveColors = new Float32Array(baseColors);
    return { positions, baseColors, flatColors, liveColors, edgeCount, edgeRadii };
  }, [graph]);

  // Force a generous bounding sphere so Line.raycast()'s early-out
  // doesn't reject hits after the sim moves vertices.
  useEffect(() => {
    if (geomRef.current) {
      geomRef.current.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1000);
    }
  }, [edgeCount]);

  // Phase accumulator for the traveling-glow comet. We integrate
  // (speed × dt) per frame so zoom-induced changes to `speedScale`
  // don't make the comet jump — only its rate-of-advance changes.
  const glowPhaseRef = useRef(0);
  const lastClockTimeRef = useRef(0);

  useFrame(({ clock }) => {
    if (!layout.posRef.current || !geomRef.current) return;
    const pos = layout.posRef.current;
    const arr = positions;

    const directionOn = showEdgeDirectionRef.current;
    const sleevesAllExpanded = sleevesExpandedRef.current === 'expanded';
    const hoveredSleeve = hoveredSleeveIndexRef.current;
    // Pre-compute the hide conditions used by BOTH the position pass and
    // the color pass below. Geometry must collapse to degenerate when
    // hidden — otherwise zero-colored line segments still render as
    // near-black streaks through the scene (transparent + opacity:0.65
    // doesn't make them disappear, just dims them).
    const hasActive = glowEdges.size > 0;
    const globalHide = !showEdgesRef.current && !hasActive && !forceEdges;

    // ----- Position pass: write geometry per (sleeve, sub-arc) -----
    for (let sleeveIdx = 0; sleeveIdx < edgeCount; sleeveIdx++) {
      const e = graph.edges[sleeveIdx];
      const si = layout.nodeIndex.get(e.source);
      const ti = layout.nodeIndex.get(e.target);
      if (si === undefined || ti === undefined) continue;

      const ax = pos[si * 3 + 0];
      const ay = pos[si * 3 + 1];
      const az = pos[si * 3 + 2];
      const bx = pos[ti * 3 + 0];
      const by = pos[ti * 3 + 1];
      const bz = pos[ti * 3 + 2];

      const moveCount = e.moveIds?.length ?? 1;
      const isSleeve = e.kind === 'move' && moveCount > 1;
      const expanded = isSleeve && (sleevesAllExpanded || hoveredSleeve === sleeveIdx);

      // Edge-level hide: this sleeve is entirely off-screen if either
      //   (a) the global edges-toggle is OFF and nothing's selected, OR
      //   (b) an active set exists and this sleeve isn't part of it.
      // In either case, every sub-arc of this sleeve is collapsed to
      // its source vertex so no segments render.
      const dir = glowEdges.get(sleeveIdx);
      const sleeveHidden = globalHide || (hasActive && !dir && !additiveGlow);
      if (sleeveHidden) {
        for (let arcIdx = 0; arcIdx < MAX_SUB_ARCS; arcIdx++) {
          const arcBase = sleeveIdx * FLOATS_PER_SLEEVE + arcIdx * FLOATS_PER_SUB_ARC;
          for (let v = 0; v < VERTS_PER_SUB_ARC; v++) {
            arr[arcBase + v * 3 + 0] = ax;
            arr[arcBase + v * 3 + 1] = ay;
            arr[arcBase + v * 3 + 2] = az;
          }
        }
        continue;
      }


      for (let arcIdx = 0; arcIdx < MAX_SUB_ARCS; arcIdx++) {
        const arcBase = sleeveIdx * FLOATS_PER_SLEEVE + arcIdx * FLOATS_PER_SUB_ARC;

        // Visibility rules:
        //   - arc 0 always visible (the default — singleton or sleeve)
        //   - arcs 1..N-1 visible only when this sleeve is expanded
        //   - arcs N..MAX always hidden (unused slots)
        let visible: boolean;
        if (arcIdx >= moveCount) {
          visible = false;
        } else if (arcIdx === 0) {
          visible = true;
        } else {
          visible = expanded;
        }

        if (!visible) {
          // Degenerate at source — zero-length segments don't render.
          for (let v = 0; v < VERTS_PER_SUB_ARC; v++) {
            arr[arcBase + v * 3 + 0] = ax;
            arr[arcBase + v * 3 + 1] = ay;
            arr[arcBase + v * 3 + 2] = az;
          }
          continue;
        }

        // Control point for this sub-arc — shared with EdgeFlow so the
        // cones ride exactly this curve (fan offsets + the bidirectional
        // two-lane separation live in subArcControl).
        const arcC = subArcControl(
          ax, ay, az,
          bx, by, bz,
          arcIdx,
          moveCount,
          expanded,
          e.dirSign ?? 0,
        );
        const arcCx = arcC.cx;
        const arcCy = arcC.cy;
        const arcCz = arcC.cz;

        // Clip the parametric range so the rendered arc terminates on
        // the surface of each endpoint's node sphere (instead of
        // plunging into the center). The bezier's velocity at t=0 is
        // 2·(C - A); travelling distance `radius` in that direction
        // corresponds to t ≈ radius / (2·|C - A|). This first-order
        // Taylor approximation is exact in the limit and tight at
        // the small t-values we use here (~0.03-0.06). For STRAIGHT
        // edges (where the control point sits on the chord, e.g. every
        // spoke into Game Over), |C - A| ≈ chordLen / 2, so the formula
        // reduces to radius / chordLen — same as a plain linear clip.
        // For BOWED edges the bezier moves faster at t=0 than along
        // the chord, so a smaller t hits the radius — the old
        // radius / chordLen formula was overshooting and leaving a
        // visible gap between the line tip and the sphere.
        const srcR = edgeRadii[sleeveIdx * 2 + 0];
        const tgtR = edgeRadii[sleeveIdx * 2 + 1];
        const dxAC = arcCx - ax;
        const dyAC = arcCy - ay;
        const dzAC = arcCz - az;
        const aToC = Math.hypot(dxAC, dyAC, dzAC) || 1;
        const dxBC = arcCx - bx;
        const dyBC = arcCy - by;
        const dzBC = arcCz - bz;
        const bToC = Math.hypot(dxBC, dyBC, dzBC) || 1;
        const tStart = Math.min(0.45, (srcR + EDGE_NODE_PADDING) / (2 * aToC));
        const tEnd = Math.max(0.55, 1 - (tgtR + EDGE_NODE_PADDING) / (2 * bToC));
        const tSpan = tEnd - tStart;

        // Sample bezier with this sub-arc's control point. First vertex
        // is at t=tStart, last at t=tEnd, evenly spaced in between.
        const itStart = 1 - tStart;
        let px = itStart * itStart * ax + 2 * itStart * tStart * arcCx + tStart * tStart * bx;
        let py = itStart * itStart * ay + 2 * itStart * tStart * arcCy + tStart * tStart * by;
        let pz = itStart * itStart * az + 2 * itStart * tStart * arcCz + tStart * tStart * bz;
        for (let s = 0; s < SEGMENTS_PER_EDGE; s++) {
          const t1 = tStart + tSpan * ((s + 1) / SEGMENTS_PER_EDGE);
          const it1 = 1 - t1;
          const nx = it1 * it1 * ax + 2 * it1 * t1 * arcCx + t1 * t1 * bx;
          const ny = it1 * it1 * ay + 2 * it1 * t1 * arcCy + t1 * t1 * by;
          const nz = it1 * it1 * az + 2 * it1 * t1 * arcCz + t1 * t1 * bz;
          arr[arcBase + s * 6 + 0] = px;
          arr[arcBase + s * 6 + 1] = py;
          arr[arcBase + s * 6 + 2] = pz;
          arr[arcBase + s * 6 + 3] = nx;
          arr[arcBase + s * 6 + 4] = ny;
          arr[arcBase + s * 6 + 5] = nz;
          px = nx;
          py = ny;
          pz = nz;
        }
      }
    }
    const posAttr = geomRef.current.getAttribute('position') as THREE.BufferAttribute;
    posAttr.array = arr;
    posAttr.needsUpdate = true;

    // ----- Color pass: per-vertex tints, active pulse, traveling glow -----
    // (hasActive + globalHide were computed above for the geometry pass)
    const pulse = GLOW_BIAS + GLOW_AMP * Math.sin(clock.elapsedTime * GLOW_HZ);
    const lastVi = VERTS_PER_SUB_ARC - 1;
    // Color gradient is only active when both the HUD toggle is ON AND
    // the module-level master gate (ENABLE_DIRECTION_GRADIENT) is true.
    // The gradient is currently shelved — the comet alone conveys
    // direction — but flipping the const reinstates it.
    const gradientActive = directionOn && ENABLE_DIRECTION_GRADIENT;
    const alwaysAccent = gradientActive ? ALWAYS_ACCENT : 0;
    const baseBuf = gradientActive ? baseColors : flatColors;
    // Comet is gated only by the HUD toggle (independent of the
    // gradient master gate). Direction OFF = static flat edges.
    const glowEnabled = directionOn;
    // Speed is ABSOLUTE in edge-length units per second — the
    // comet takes ~1/GLOW_TRAVEL_SPEED seconds to traverse one edge
    // regardless of camera zoom. The previous zoom-distance scaling
    // made the comets speed up at far zoom and slow down at close
    // zoom, which read as jitter during a zoom drag. dt is still
    // clamped to a 0.25s ceiling so a tab being backgrounded
    // doesn't lurch the phase forward when it returns.
    const time = clock.elapsedTime;
    const dt = Math.max(0, Math.min(0.25, time - lastClockTimeRef.current));
    lastClockTimeRef.current = time;
    glowPhaseRef.current += dt * GLOW_TRAVEL_SPEED;
    const baseGlowPhase = glowPhaseRef.current;

    const focalMoveId = focalMoveIdRef.current;
    for (let sleeveIdx = 0; sleeveIdx < edgeCount; sleeveIdx++) {
      const e = graph.edges[sleeveIdx];
      const moveCount = e.moveIds?.length ?? 1;
      const isSleeve = e.kind === 'move' && moveCount > 1;
      const expanded = isSleeve && (sleevesAllExpanded || hoveredSleeve === sleeveIdx);
      const dir = glowEdges.get(sleeveIdx);

      // When the focal move (hover-preview or move-selection) lives
      // inside THIS expanded sleeve, find its sub-arc index. Other
      // sub-arcs in the same sleeve become "siblings": still visible
      // but recessed (dim). focalArcIdx === -1 means this sleeve has
      // no specific focal sub-arc — treat the whole sleeve normally.
      let focalArcIdx = -1;
      if (expanded && focalMoveId && e.moveIds) {
        const i = e.moveIds.indexOf(focalMoveId);
        if (i >= 0) focalArcIdx = i;
      }

      for (let arcIdx = 0; arcIdx < MAX_SUB_ARCS; arcIdx++) {
        const arcBase = sleeveIdx * FLOATS_PER_SLEEVE + arcIdx * FLOATS_PER_SUB_ARC;

        // Hidden sub-arcs: zero out colors so the (degenerate) segments
        // contribute nothing even if they manage to render.
        let visible: boolean;
        if (arcIdx >= moveCount) {
          visible = false;
        } else if (arcIdx === 0) {
          visible = true;
        } else {
          visible = expanded;
        }
        // Global hide ("edges: off" toggle + idle state): nothing visible.
        if (!visible || globalHide) {
          for (let j = 0; j < FLOATS_PER_SUB_ARC; j++) {
            liveColors[arcBase + j] = 0;
          }
          continue;
        }

        // Sibling-in-sleeve dim: when a specific sub-arc is the focal
        // one (hover-preview or move-selection lands on it), every
        // OTHER sub-arc in the same sleeve dims to SLEEVE_SIBLING_DIM
        // so the focal arc reads as the explicit highlight without
        // making the siblings disappear entirely.
        if (focalArcIdx >= 0 && arcIdx !== focalArcIdx) {
          for (let j = 0; j < FLOATS_PER_SUB_ARC; j += 3) {
            liveColors[arcBase + j + 0] = baseBuf[arcBase + j + 0] * SLEEVE_SIBLING_DIM;
            liveColors[arcBase + j + 1] = baseBuf[arcBase + j + 1] * SLEEVE_SIBLING_DIM;
            liveColors[arcBase + j + 2] = baseBuf[arcBase + j + 2] * SLEEVE_SIBLING_DIM;
          }
          continue;
        }

        // Non-incident edge when an active set is in play → hide. (Skip
        // under additiveGlow so the gameplan line highlight keeps the
        // rest of the plan's edges visible at base color.)
        if (hasActive && !dir && !additiveGlow) {
          for (let j = 0; j < FLOATS_PER_SUB_ARC; j += 3) {
            liveColors[arcBase + j + 0] = baseBuf[arcBase + j + 0] * DIM_FACTOR;
            liveColors[arcBase + j + 1] = baseBuf[arcBase + j + 1] * DIM_FACTOR;
            liveColors[arcBase + j + 2] = baseBuf[arcBase + j + 2] * DIM_FACTOR;
          }
          continue;
        }

        // Per-(sleeve, sub-arc) phase offset for the comet — pseudo-random
        // mix so the glow scatters across the scene rather than pulsing
        // in lockstep.
        const glowPhase =
          (baseGlowPhase + sleeveIdx * 0.137 + arcIdx * 0.291) % 1;

        for (let vi = 0; vi < VERTS_PER_SUB_ARC; vi++) {
          const t = vi / lastVi;
          const idx = arcBase + vi * 3;
          const bR = baseBuf[idx + 0];
          const bG = baseBuf[idx + 1];
          const bB = baseBuf[idx + 2];

          // ---- Compute base color for this vertex (before glow) ----
          let r: number;
          let g: number;
          let bCol: number;

          if (!gradientActive) {
            // Flat mode (either direction OFF or the gradient master
            // gate is disabled). Active incident edges still get a
            // white pulse so selection is visible.
            if (dir === 'out' || dir === 'in') {
              const intensity =
                dir === 'out'
                  ? Math.pow(1 - t, DIR_FALLOFF) * pulse
                  : Math.pow(t, DIR_FALLOFF) * pulse;
              r = bR + (1 - bR) * intensity;
              g = bG + (1 - bG) * intensity;
              bCol = bB + (1 - bB) * intensity;
            } else if (dir === 'neutral') {
              const blend = pulse;
              r = bR + (1 - bR) * blend;
              g = bG + (1 - bG) * blend;
              bCol = bB + (1 - bB) * blend;
            } else {
              r = bR;
              g = bG;
              bCol = bB;
            }
          } else {
            // Direction ON: warm/cool gradient + active-end pulse.
            const warmBaseCurve = 1 - t;
            const coolBaseCurve = t;
            let warmI = warmBaseCurve * alwaysAccent;
            let coolI = coolBaseCurve * alwaysAccent;

            if (dir === 'out') {
              warmI = Math.pow(1 - t, DIR_FALLOFF) * pulse;
            } else if (dir === 'in') {
              coolI = Math.pow(t, DIR_FALLOFF) * pulse;
            }

            if (dir === 'neutral') {
              const blend = pulse;
              r = bR + (1 - bR) * blend;
              g = bG + (1 - bG) * blend;
              bCol = bB + (1 - bB) * blend;
            } else {
              r = bR + (OUTGOING_ACCENT.r - bR) * warmI + (INCOMING_ACCENT.r - bR) * coolI;
              g = bG + (OUTGOING_ACCENT.g - bG) * warmI + (INCOMING_ACCENT.g - bG) * coolI;
              bCol = bB + (OUTGOING_ACCENT.b - bB) * warmI + (INCOMING_ACCENT.b - bB) * coolI;
            }
          }

          // ---- Traveling glow on top (always on) ----
          // Bright head at glowPhase, fading trail behind (lower t).
          // Blends toward white. This is the primary motion-based
          // directionality cue, runs regardless of the direction toggle.
          if (glowEnabled) {
            let dt = glowPhase - t;
            if (dt < 0) dt += 1;
            if (dt < 0.5) {
              const fall = dt * GLOW_TRAVEL_FALLOFF;
              const glowI = Math.exp(-(fall * fall)) * GLOW_TRAVEL_AMOUNT;
              r += (1 - r) * glowI;
              g += (1 - g) * glowI;
              bCol += (1 - bCol) * glowI;
            }
          }

          liveColors[idx + 0] = r;
          liveColors[idx + 1] = g;
          liveColors[idx + 2] = bCol;
        }
      }
    }

    const colorAttr = geomRef.current.getAttribute('color') as THREE.BufferAttribute;
    colorAttr.needsUpdate = true;
  });

  const totalVerts = edgeCount * MAX_SUB_ARCS * VERTS_PER_SUB_ARC;

  return (
    <lineSegments
      onPointerOver={onPointerOver}
      onPointerOut={onPointerOut}
      onClick={onClick}
    >
      <bufferGeometry ref={geomRef}>
        <bufferAttribute
          attach="attributes-position"
          args={[positions, 3]}
          count={totalVerts}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-color"
          args={[liveColors, 3]}
          count={totalVerts}
          itemSize={3}
        />
      </bufferGeometry>
      <lineBasicMaterial
        vertexColors
        transparent
        // 0.65 → 0.82: edges read more clearly against the dark
        // canvas, especially at far zoom. They're still translucent
        // enough that they don't overwhelm node geometry; the comet
        // and direction tints continue to pop on top.
        opacity={0.82}
        toneMapped={false}
        // linewidth is browser-dependent: WebGL on Chrome/Edge/Mac
        // Safari clamps to 1px regardless, but Firefox and some
        // WebGPU paths honor it. Setting 2 gives a free upgrade
        // where the platform supports it without breaking the
        // others. For guaranteed-thick edges we'd need Line2 from
        // three-stdlib, which would require rewriting this whole
        // file — deferred.
        linewidth={2}
      />
    </lineSegments>
  );
}
